import type { EntityDef } from "@repo/kernel"

/**
 * Per-runtime registry of entity defs by key. The static dispatch server
 * functions resolve a def server-side from the `entityKey` in the request, so
 * the def must be registered on the server — `createEntityModule` registers it
 * at module load, which requires the module to be imported server-side.
 */
const registry = new Map<string, EntityDef>()
/**
 * Table SQL name → EVERY def registered against it. Several defs may share one
 * table — a `discriminator` narrows a table into a view — so a single slot
 * would keep whichever module imported last and silently answer for the rest.
 */
const byTable = new Map<string, EntityDef[]>()

/** Registers an entity def under its key + table (called by `createEntityModule`). */
export function registerEntityDef(def: EntityDef): void {
  // `reveal` ops return one-time secrets — fail fast on shapes that would leak
  // them: a std CRUD result is row data, and `emits` would put the raw result
  // on the event queue.
  for (const [name, op] of Object.entries(def.operations ?? {})) {
    if (!op.reveal) continue
    if (!op.custom && !op.handler) {
      throw new Error(
        `Operation "${name}" on "${def.key}" declares reveal but has no custom handler`,
      )
    }
    if (op.emits) {
      throw new Error(
        `Operation "${name}" on "${def.key}" declares reveal with emits — emit a scrubbed payload from the handler instead`,
      )
    }
  }
  // Re-registering a key (HMR, a test) replaces it in place — and a key that
  // moved to another table leaves the old table's list, or it would keep
  // answering for a table it no longer describes.
  const previous = registry.get(def.key)
  if (previous && previous.table !== def.table) {
    byTable.set(
      previous.table,
      defsForTable(previous.table).filter((other) => other.key !== def.key),
    )
  }
  registry.set(def.key, def)
  const defs = byTable.get(def.table) ?? []
  const at = defs.findIndex((other) => other.key === def.key)
  if (at === -1) defs.push(def)
  else defs[at] = def
  byTable.set(def.table, defs)
}

/** Resolves a registered entity def, or throws if its module wasn't imported. */
export function resolveEntityDef(key: string): EntityDef {
  const def = registry.get(key)
  if (!def) {
    throw new Error(
      `No entity registered for key "${key}". Ensure its module is imported on the server.`,
    )
  }
  return def
}

/** Every registered def over this SQL table, in registration order. */
export function defsForTable(table: string): EntityDef[] {
  return byTable.get(table) ?? []
}

/**
 * The def that answers for a SQL table (materialization). A def without a base
 * condition describes the whole table, so it wins over the discriminated views
 * beside it; failing that, the first registered — either way the answer never
 * depends on which module happened to import last.
 */
export function defForTable(table: string): EntityDef | undefined {
  const defs = defsForTable(table)
  return defs.find((def) => !def.discriminator && !def.baseFilter) ?? defs[0]
}

/** The registered def for this key, if any (non-throwing; for materialization). */
export function defForKey(key: string): EntityDef | undefined {
  return registry.get(key)
}
